import { Grid, GridProps, Heading, HStack, ResponsiveValue, Stack, Text } from "@biblioteksentralen/react";
import { Publication, PublicationListItem, SanityKeyed } from "@libry-content/types";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { DefaultContainer } from "../../ContentContainer";
import { Edit } from "../../editInSanity/EditInSanity";
import CreatedByTag from "../../employees/CreatedByTag";
import { TargetAudienceTags } from "../MediaTypesAndTargetAudiences";
import PublicationView from "./Publication";
import { ResolvedList } from "@libry-content/common";

type PublicationsGridProps = GridProps & {
  publications?: SanityKeyed<Publication | PublicationListItem>[];
  minPublicationWidth?: ResponsiveValue<string>;
  publicationHeadingSize?: "h2" | "h3";
  showPublicationTeaser?: boolean;
};

export const PublicationsGrid = ({
  publications,
  minPublicationWidth = "16rem",
  gridGap = "3rem 1rem",
  publicationHeadingSize = "h3",
  showPublicationTeaser,
  ...rest
}: PublicationsGridProps) => {
  if (!publications?.length) return null;
  return (
    <Grid
      templateColumns={`repeat(auto-fill, minmax(${minPublicationWidth}, 1fr))`}
      gridGap={gridGap}
      alignItems="end"
      overflow="hidden"
      {...rest}
    >
      {publications?.map((publication) => (
        <PublicationView
          item={publication}
          key={publication._key}
          headingSize={publicationHeadingSize}
          showPublicationTeaser={showPublicationTeaser}
        />
      ))}
    </Grid>
  );
};

interface Props {
  list?: ResolvedList;
}

const BookListView = ({ list }: Props) => {
  const { ts } = useTranslation();

  if (!list) return null;

  return (
    <DefaultContainer margin="0 auto 5rem">
      <Stack gridGap="1.5rem" paddingBottom="4rem" maxWidth="35rem">
        <Stack>
          <Heading as="h1" size="lg">
            {ts(list.title)}
          </Heading>
          <HStack spacing={1}>
            <TargetAudienceTags targetAudiences={list.targetAudiences} />
          </HStack>
        </Stack>
        <Stack spacing="1rem">
          <Heading as="h2" size="md" fontWeight="normal">
            {ts(list.teaser)}
          </Heading>
          <Text>{ts(list.description)}</Text>
          <CreatedByTag
            createdBy={list.createdBy}
            size="md"
            paletteColor={{ color: "white", backgroundColor: "black" }}
          />
        </Stack>
      </Stack>

      <Stack direction="column" spacing="2rem">
        <Edit doc={list} />
        <PublicationsGrid
          publications={list.items}
          publicationHeadingSize="h2"
          showPublicationTeaser={list.publicationTeaserToggle}
        />
      </Stack>
    </DefaultContainer>
  );
};

export default BookListView;
